import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Toast,
  ToastContainer,
} from "react-bootstrap";

import { useUser } from "../context/UserContext";
import config from "../devprodConfig";

import Signature from "../components/Signature";

import useUserProfile from "../components/hooks/useUserProfile";

import DeviceManager from "../components/userProfile/DeviceManager";

import ItDeviceManager from "../components/userProfile/ItDeviceManager";

import UserProfileEditor from "../components/userProfile/UserProfileEditor";



import {
  createKoulutus,
  updateKoulutus,
  deleteKoulutus,

  createKurssi,
  updateKurssi,
  deleteKurssi,

  createPatevyys,
  updatePatevyys,
  deletePatevyys,

  createKielitaito,
  updateKielitaito,
  deleteKielitaito,

  createTyokokemus,
  updateTyokokemus,
  deleteTyokokemus,

  createReferenssi,
  updateReferenssi,
  deleteReferenssi,

  createYritysReferenssi,
  updateYritysReferenssi,
  deleteYritysReferenssi,
} from "../api/api";

function Profile() {
  const navigate = useNavigate();

  const {
    meUser,
    localUser,
    loading,
    refreshLocalUser,
  } = useUser();

  // ============================================================
  // USER
  // ============================================================
const [activeProfileTab, setActiveProfileTab] =
  useState("perustiedot");

  const [formData, setFormData] =
    useState({});

  const [saving, setSaving] =
    useState(false);

  const [toast, setToast] =
    useState({
      show: false,
      message: "",
      bg: "",
    });

  // ============================================================
  // SHARED PROFILE DATA
  // ============================================================

  const profile =
    useUserProfile(formData.id);

  // ============================================================
  // TOAST
  // ============================================================

  const showToast = (
    message,
    bg = "success"
  ) => {
    setToast({
      show: true,
      message,
      bg,
    });
  };

  // ============================================================
  // INITIALIZE CURRENT USER
  // ============================================================

  useEffect(() => {
    if (!loading && meUser) {
      setFormData({
        id: meUser.id,

        sahkoposti:
          meUser.sahkoposti || "",

        nimi:
          meUser.nimi || "",

        puhelinnumero:
          meUser.puhelinnumero || "",

        ammattinimike:
          meUser.ammattinimike || "",

        yritys:
          meUser.yritys || "",

        osoite:
          meUser.osoite || "",

        postinumero:
          meUser.postinumero || "",

        toimipaikka:
          meUser.toimipaikka || "",

        rooli:
          meUser.rooli || "vieras",
      });
    }
  }, [loading, meUser]);

  // ============================================================
  // FIELD CHANGE
  // ============================================================

  const handleProfileFieldChange = (
    field,
    value
  ) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  // ============================================================
  // SAVE PROFILE
  // ============================================================

  const handleSave = async () => {
    if (!formData?.id) {
      showToast(
        "❌ Käyttäjän ID puuttuu",
        "danger"
      );

      return;
    }

    if (!formData.nimi?.trim()) {
      showToast(
        "❌ Nimi vaaditaan",
        "danger"
      );

      return;
    }

    try {
      setSaving(true);

      // ========================================================
      // 1. BASIC USER DATA
      // ========================================================

      const userPayload = {
        nimi:
          formData.nimi?.trim() || "",

        sahkoposti:
          formData.sahkoposti || "",

        puhelinnumero:
          formData.puhelinnumero || "",

        ammattinimike:
          formData.ammattinimike || "",

        yritys:
          formData.yritys || "",

        osoite:
          formData.osoite || "",

        postinumero:
          formData.postinumero || "",

        toimipaikka:
          formData.toimipaikka || "",

        /*
         * Normal user cannot change
         * their own role from Profile.
         */
        rooli:
          meUser?.rooli ||
          formData.rooli ||
          "vieras",
      };

      const response = await fetch(
        `${config.apiBaseUrl}/api/users/${formData.id}`,
        {
          method: "PUT",

          headers: {
            "Content-Type":
              "application/json",
          },

          credentials: "include",

          body: JSON.stringify(
            userPayload
          ),
        }
      );

      if (!response.ok) {
        const errorText =
          await response.text();

        throw new Error(
          errorText ||
            `Käyttäjän päivitys epäonnistui (${response.status})`
        );
      }

      // ========================================================
      // 2. DELETE REMOVED ROWS
      // ========================================================

      await Promise.all([
        ...profile.deletedKoulutusIds.map(
          (id) =>
            deleteKoulutus(id)
        ),

        ...profile.deletedKurssiIds.map(
          (id) =>
            deleteKurssi(id)
        ),

        ...profile.deletedPatevyysIds.map(
          (id) =>
            deletePatevyys(id)
        ),

        ...profile.deletedKielitaitoIds.map(
          (id) =>
            deleteKielitaito(id)
        ),

        ...profile.deletedTyokokemusIds.map(
          (id) =>
            deleteTyokokemus(id)
        ),

        ...profile.deletedReferenssit.map(
          (id) =>
            deleteReferenssi(id)
        ),

        ...profile.deletedYritysReferenssit.map(
          (id) =>
            deleteYritysReferenssi(id)
        ),
      ]);

      // ========================================================
      // 3. KOULUTUKSET
      // ========================================================

    for (
  const [index, item] of
  profile.koulutukset.entries()
) {
  const data = {
    tutkinto:
      item.tutkinto || "",

    oppilaitos:
      item.oppilaitos || "",

    valmistumisvuosi:
      item.valmistumisvuosi ||
      null,

    jarjestys: index,
  };

  if (item.id) {
    await updateKoulutus(
      item.id,
      data
    );
  } else {
    await createKoulutus(
      formData.id,
      data
    );
  }
}

      // ========================================================
      // 4. KURSSIT
      // ========================================================

      for (
  const [index, item] of
  profile.kurssit.entries()
) {
  const data = {
    kurssi:
      item.kurssi || "",

    jarjestaja:
      item.jarjestaja || "",

    suoritusvuosi:
      item.suoritusvuosi ||
      null,

    jarjestys: index,
  };

  if (item.id) {
    await updateKurssi(
      item.id,
      data
    );
  } else {
    await createKurssi(
      formData.id,
      data
    );
  }
}

      // ========================================================
      // 5. PÄTEVYYDET
      // ========================================================

      for (
  const [index, item] of
  profile.patevyydet.entries()
) {
  const data = {
    patevyys:
      item.patevyys || "",

    myontaja:
      item.myontaja || "",

    suoritusvuosi:
      item.suoritusvuosi ||
      null,

    jarjestys: index,
  };

  if (item.id) {
    await updatePatevyys(
      item.id,
      data
    );
  } else {
    await createPatevyys(
      formData.id,
      data
    );
  }
}
      // ========================================================
      // 6. KIELITAIDOT
      // ========================================================

      for (
  const [index, item] of
  profile.kielitaidot.entries()
) {
  const data = {
    kieli:
      item.kieli || "",

    taitotaso:
      item.taitotaso ||
      "Hyvä",

    jarjestys: index,
  };

  if (item.id) {
    await updateKielitaito(
      item.id,
      data
    );
  } else {
    await createKielitaito(
      formData.id,
      data
    );
  }
}

     // ========================================================
// 7. TYÖKOKEMUS
// ========================================================

for (
  const [index, item] of
  profile.tyokokemukset.entries()
) {
  const data = {
    tyotehtava:
      item.tyotehtava || "",

    yritys:
      item.yritys || "",

    alkamisvuosi:
      item.alkamisvuosi || null,

    paattymisvuosi:
      item.paattymisvuosi || null,

    jarjestys: index,
  };

  if (item.id) {
    await updateTyokokemus(
      item.id,
      data
    );
  } else {
    await createTyokokemus(
      formData.id,
      data
    );
  }
}

     // ========================================================
// 8. PERSONAL REFERENCES
// ========================================================

for (
  const [index, item] of
  profile.referenssit.entries()
) {
  const data = {
    /*
     * "Muu" is only a UI state.
     * Don't store literal "Muu"
     * if nothing was entered.
     */
    kohde:
      item.kohde === "Muu"
        ? ""
        : item.kohde || "",

    tilaaja:
      item.tilaaja || "",

    tuote:
      item.tuote === "Muu"
        ? ""
        : item.tuote || "",

    toteutusvuosi:
      item.toteutusvuosi || null,

    jarjestys: index,
  };

  if (item.id) {
    await updateReferenssi(
      item.id,
      data
    );
  } else {
    await createReferenssi(
      formData.id,
      data
    );
  }
}

// ========================================================
// 9. COMPANY REFERENCES
// ========================================================

for (
  const [index, item] of
  profile.yritysReferenssit.entries()
) {
  const data = {
    kohde:
      item.kohde === "Muu"
        ? ""
        : item.kohde || "",

    tilaaja:
      item.tilaaja || "",

    tuote:
      item.tuote === "Muu"
        ? ""
        : item.tuote || "",

    toteutusvuosi:
      item.toteutusvuosi || null,

    jarjestys: index,
  };

  if (item.id) {
    await updateYritysReferenssi(
      item.id,
      data
    );
  } else {
    await createYritysReferenssi(
      data
    );
  }
}
      // ========================================================
      // 10. RELOAD PROFILE
      // ========================================================
profile.resetDeleted();

await profile.reload();

showToast(
  "✅ Käyttäjätiedot päivitetty",
  "success"
);
    } catch (error) {
      console.error(
        "❌ Tallennus epäonnistui:",
        error
      );

      showToast(
        "❌ Tallennus epäonnistui",
        "danger"
      );
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (
    loading ||
    !formData.sahkoposti
  ) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" />

        <p className="mt-2">
          Ladataan profiilia...
        </p>
      </Container>
    );
  }

  // ============================================================
  // UI
  // ============================================================

  return (
    <>
      <Container className="mt-5 mb-5">
        <Row className="justify-content-center">
          <Col
            xs={12}
            xl={10}
          >
            <Card
              className="shadow rounded-4 border-0"
              style={{
                overflow: "hidden",
              }}
            >
              {/* =================================================
                  HEADER
              ================================================= */}

              <div
                className="p-4"
                style={{
                  backgroundColor:
                    "#04aa00",

                  color:
                    "#ffffff",
                }}
              >
                <h3 className="mb-1">
                  Käyttäjätiedot
                </h3>

                <div
                  style={{
                    opacity: 0.9,
                  }}
                >
                  {formData.nimi}
                </div>
              </div>

              <Card.Body className="p-4">
                {/* ===============================================
                    PROFILE LOADING
                =============================================== */}

               {profile.loading && (
  <div className="text-center py-3">
    <Spinner
      animation="border"
      size="sm"
    />

    <span className="ms-2">
      Ladataan tietoja...
    </span>
  </div>
)}

<>
  {/* ===========================================
      SHARED PROFILE EDITOR
  =========================================== */}

  <UserProfileEditor
    user={formData}
    onUserChange={handleProfileFieldChange}
    profile={profile}
    showRole={false}
activeTab={activeProfileTab}
  setActiveTab={setActiveProfileTab}
    renderBasicExtra={() => (
      <>
        {/* ALLEKIRJOITUS */}

        <div
          className="mt-4"
          style={{
            border: "1px solid #e1e8e1",
            borderRadius: "12px",
            padding: "20px",
            backgroundColor: "#ffffff",
          }}
        >
          <div
            className="fw-semibold mb-3"
            style={{
              color: "#264d26",
            }}
          >
            Allekirjoitus
          </div>

          <Signature
            userId={formData.id}
            onAutographSaved={() =>
              showToast(
                "Allekirjoitus tallennettu",
                "success"
              )
            }
          />
        </div>

        {/* ADMIN */}

        {localUser?.rooli === "admin" && (
          <div className="mt-4">
            <Button
              type="button"
              variant="outline-success"
              onClick={() =>
                navigate(
                  "/kayttajahallinta"
                )
              }
            >
              Avaa käyttäjien hallinta
            </Button>
          </div>
        )}
      </>
    )}

    renderDevices={() => (
      <DeviceManager
        userId={formData.id}
        users={profile.users}
        showToast={showToast}
      />
    )}

    renderItDevices={() => (
      <ItDeviceManager
        userId={formData.id}
        showToast={showToast}
      />
    )}
  />

  {/* ===========================================
      SAVE
  =========================================== */}

  <div
    className="d-flex justify-content-end mt-4 pt-4"
    style={{
      borderTop:
        "1px solid #e7ece7",
    }}
  >
    <Button
      type="button"
      disabled={saving}
      onClick={handleSave}
      style={{
        minWidth: "180px",
        backgroundColor:
          "#04aa00",
        borderColor:
          "#04aa00",
        borderRadius:
          "9px",
        fontWeight:
          "600",
      }}
    >
      {saving ? (
        <>
          <Spinner
            animation="border"
            size="sm"
            className="me-2"
          />

          Tallennetaan...
        </>
      ) : (
        "Tallenna muutokset"
      )}
    </Button>
  </div>
</>
                
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ============================================================
          TOAST
      ============================================================ */}

      <ToastContainer
        position="bottom-end"
        className="p-3"
      >
        <Toast
          show={toast.show}
          bg={toast.bg}
          autohide
          delay={3500}
          onClose={() =>
            setToast(
              (previous) => ({
                ...previous,
                show: false,
              })
            )
          }
        >
          <Toast.Body
            className={
              toast.bg ===
                "danger" ||
              toast.bg ===
                "success"
                ? "text-white"
                : ""
            }
          >
            {toast.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
}

export default Profile;